import { useState, useEffect } from 'react';

import Link from 'next/link';
import { useRouter } from 'next/router';

import {
  useMediaQuery,
  colors,
  Grid,
  FormControl,
  OutlinedInput,
  InputAdornment,
  Typography,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';
import { LoadingButton } from '@material-ui/lab';

import PropTypes from 'prop-types';

import { Image, Icon } from '@/src/components/atoms';
import { SectionHeader } from '@/src/components/molecules';

import ChipCategory from './ChipCategory';

const useStyles = makeStyles(theme => ({
  image: {
    width: 500,
    [theme.breakpoints.down('sm')]: {
      maxWidth: 400,
    },
  },
  searchInputContainer: {
    background: theme.palette.alternate.main,
    padding: theme.spacing(2),
    boxShadow: '0 4px 14px 0 rgba(0, 0, 0, 0.11)',
    borderRadius: theme.spacing(1),
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    '& .MuiOutlinedInput-notchedOutline': {
      border: '0 !important',
    },
    '& .MuiInputAdornment-positionStart': {
      marginRight: theme.spacing(2),
    },
    '& .MuiOutlinedInput-adornedStart': {
      paddingLeft: 0,
    },
    '& .MuiOutlinedInput-input': {
      padding: 0,
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1),
    },
  },
  searchButton: {
    maxHeight: 45,
    minWidth: 135,
    [theme.breakpoints.down('sm')]: {
      minWidth: 'auto',
    },
  },
  tags: {
    display: 'flex',
    flexWrap: 'wrap',
  },
}));

function Hero({ categories, loading }) {
  const router = useRouter();
  const classes = useStyles();
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'), { defaultMatches: true });
  const [input, setInput] = useState('');

  const { search = '' } = router.query;

  const handleFind = () =>
    router.push({ pathname: '/blog', query: { search: input } }, undefined, {
      shallow: true,
      scroll: false,
    });

  useEffect(() => {
    setInput(search);
  }, [search]);

  return (
    <Grid container justifyContent="space-between" spacing={isMd ? 4 : 2} alignItems="center">
      <Grid item xs={12} md={6} data-aos="fade-up">
        <SectionHeader
          title={
            <span>
              Conheça como estamos ajudando a transformar a{' '}
              <Typography component="span" variant="inherit" color="primary">
                educação do Brasil
              </Typography>
            </span>
          }
          subtitle="Tudo sobre e-commerce e marketplace escolar, transformação digital das relações comerciais e financeiras entre instituições de ensino, fornecedores e famílias."
          align={isMd ? 'left' : 'center'}
          disableGutter
          titleVariant="h4"
        />
      </Grid>
      <Grid item container justifyContent="center" xs={12} md={6} data-aos="fade-up">
        <Image
          src="/images/illustrations/blog/hero.svg"
          alt="Ilustração com pessoas acessando seus dispositivos"
          className={classes.image}
        />
      </Grid>
      <Grid item xs={12}>
        <div className={classes.searchInputContainer}>
          <FormControl fullWidth variant="outlined">
            <OutlinedInput
              startAdornment={
                <InputAdornment position="start">
                  <Icon fontIconClass="fas fa-search" fontIconColor={colors.blueGrey[900]} />
                </InputAdornment>
              }
              placeholder="Buscar artigos"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyPress={ev => {
                if (ev.key === 'Enter') {
                  handleFind();
                  ev.preventDefault();
                }
              }}
            />
          </FormControl>
          <LoadingButton
            loading={loading}
            loadingPosition="end"
            endIcon={<SearchIcon />}
            color="primary"
            variant="contained"
            size="large"
            className={classes.searchButton}
            onClick={handleFind}>
            Buscar
          </LoadingButton>
        </div>
      </Grid>
      <Grid item xs={12}>
        <div className={classes.tags}>
          {categories?.results.map(category => (
            <Link key={category.id} href={['/blog/category', category.uid].join('/')} passHref>
              <ChipCategory
                label={category.data?.name}
                sx={{ backgroundColor: category.data?.color }}
              />
            </Link>
          ))}
        </div>
      </Grid>
    </Grid>
  );
}

Hero.propTypes = {
  categories: PropTypes.object,
  loading: PropTypes.bool,
};

export default Hero;
